import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useState } from 'react';
import { useChatActions, useChatConfig } from '@/ui/ChatProvider';
import { Markdown, isTrailingQuestion } from './Markdown';
import { DataChart } from './DataChart';
/**
 * Renders a reply.
 *
 * The flat `reply` string is the primary path: the backend already writes it
 * as Markdown — headings, lists, GFM tables and all — so rendering it directly
 * keeps a live reply and the same reply after a reload identical.
 *
 * The structured `content` blocks are used when `reply` comes back empty, and
 * whenever they carry STRUCTURE the Markdown cannot: KPI tiles, a chart, a
 * details panel or a typed table (a data answer). Its Markdown is then only
 * the plain twin for clients without blocks.
 */
export function MessageContent({ blocks, text, emphasizeQuestions }) {
    const structured = hasStructure(blocks);
    if (!structured && (text.trim() || !blocks?.length)) {
        return _jsx(Markdown, { text: text, emphasizeTrailingQuestion: emphasizeQuestions });
    }
    const list = blocks ?? [];
    // Only the final text block is a candidate for the heuristic, and only when
    // the backend hasn't already marked a question explicitly.
    const hasExplicitQuestion = list.some((block) => block.type === 'text' && block.role === 'question');
    const lastTextIndex = list.reduce((last, block, index) => (block.type === 'text' ? index : last), -1);
    return (_jsx(_Fragment, { children: list.map((block, index) => {
            const key = block.id ?? index;
            switch (block.type) {
                case 'table':
                    return _jsx(TableBlock, { block: block }, key);
                case 'kpi':
                    return _jsx(KpiBlock, { items: block.items }, key);
                case 'chart':
                    return _jsx(DataChart, { block: block }, key);
                case 'details':
                    return _jsx(DetailsBlock, { title: block.title, items: block.items }, key);
                case 'action':
                    return _jsx(ActionButton, { action: block.action, label: block.label }, key);
                case 'text': {
                    // An explicit role beats guessing. Otherwise only the final text block
                    // is a candidate, and only when something precedes it.
                    const guessing = emphasizeQuestions && !hasExplicitQuestion && index === lastTextIndex;
                    const blockIsWhollyAQuestion = guessing && index > 0 && isTrailingQuestion(block.text);
                    return (_jsx(Fragment, { children: _jsx(Markdown, { text: block.text, 
                            // The whole block is the question, or it ends with one.
                            forcePrompt: (emphasizeQuestions && block.role === 'question') || blockIsWhollyAQuestion, emphasizeTrailingQuestion: guessing && !blockIsWhollyAQuestion }) }, key));
                }
                default:
                    return null;
            }
        }) }));
}
function hasStructure(blocks) {
    return !!blocks?.some((block) => block.type === 'kpi' ||
        block.type === 'chart' ||
        block.type === 'details' ||
        block.type === 'action' ||
        (block.type === 'table' && !!block.columnTypes?.length));
}
const NUMERIC = new Set(['money', 'count', 'number', 'pct']);
function TableBlock({ block }) {
    const columnCount = Math.max(block.columns.length, ...block.rows.map((row) => row.length), 0);
    if (!columnCount)
        return null;
    const numeric = (j) => !!block.columnTypes && NUMERIC.has(block.columnTypes[j] ?? 'text');
    const total = block.totalRows ?? block.rows.length;
    const more = block.truncated || total > block.rows.length;
    return (_jsxs("div", { className: "ac-data-table", children: [_jsx("div", { className: "ac-table-scroll", role: "region", "aria-label": block.caption ?? 'Table', tabIndex: 0, children: _jsxs("table", { className: "ac-table", children: [block.caption && _jsx("caption", { children: block.caption }), !!block.columns.length && (_jsx("thead", { children: _jsx("tr", { children: block.columns.map((column, i) => (_jsx("th", { scope: "col", className: numeric(i) ? 'ac-num' : undefined, children: column }, i))) }) })), _jsx("tbody", { children: block.rows.map((row, i) => (_jsx("tr", { children: Array.from({ length: columnCount }, (_, j) => (_jsx("td", { className: numeric(j) ? 'ac-num' : undefined, children: row[j] ?? '' }, j))) }, i))) })] }) }), (more || block.resultId) && (_jsxs("div", { className: "ac-table-foot", children: [_jsx("span", { children: more
                            ? block.truncated
                                ? `Showing the first ${block.rows.length} rows; there are more.`
                                : `Showing ${block.rows.length} of ${total} rows.`
                            : `${total} rows.` }), block.resultId && _jsx(DownloadButton, { resultId: block.resultId })] }))] }));
}
function DownloadButton({ resultId }) {
    const { downloadResult } = useChatActions();
    const [state, setState] = useState('idle');
    return (_jsx("button", { type: "button", className: "ac-link-button", disabled: state === 'busy', onClick: async () => {
            setState('busy');
            setState((await downloadResult(resultId)) ? 'idle' : 'failed');
        }, children: state === 'busy' ? 'Preparing…' : state === 'failed' ? 'Download failed — retry' : 'Download CSV' }));
}
/**
 * Something the widget does, offered in an answer. `open_data_mode`: the
 * private data chat, when the host page set one up (`dataMode`); otherwise
 * nothing is shown and the answer's text still says where to go.
 */
function ActionButton({ action, label }) {
    const config = useChatConfig();
    const run = action === 'open_data_mode' ? config.onOpenDataMode : undefined;
    if (!run)
        return null;
    return (_jsxs("button", { type: "button", className: "ac-action-button", onClick: run, children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD12" }), " ", label] }));
}
function KpiBlock({ items }) {
    return (_jsx("div", { className: "ac-kpis", "data-count": Math.min(items.length, 4), children: items.map((item, i) => (_jsxs("div", { className: "ac-kpi", children: [_jsx("div", { className: "ac-kpi-value", children: item.display }), _jsx("div", { className: "ac-kpi-label", children: item.label })] }, i))) }));
}
/** SQL shown by a tenant that turned it on: a block of code, not a sentence. */
const SQL = /^\s*(select|with)\b/i;
function DetailsBlock({ title, items }) {
    return (_jsxs("details", { className: "ac-details", children: [_jsx("summary", { children: title }), _jsx("ul", { children: items.map((item, i) => SQL.test(item) ? (_jsx("li", { className: "ac-details-code", children: _jsx("pre", { children: _jsx("code", { children: item }) }) }, i)) : (_jsx("li", { children: item }, i))) })] }));
}
